import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Edit2, Save, X, CheckCircle, Sparkles, BarChart3, AlertTriangle, Trash2, Loader2, Camera, ChevronDown, Building2, Heart, MapPin, Users, Calendar, User } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { usePaymentsVisibility } from '../hooks/usePaymentsVisibility';
import { useNavigate, useLocation } from 'react-router-dom';
import { showToast } from '../utils/errorHandler';
import RecruiterLoggedOutGate from '../components/RecruiterLoggedOutGate';
import { RecruiterInlineEmpty, RecruiterPageShell } from '../components/Recruiter/RecruiterStates';
import { useTranslation } from 'react-i18next';
import { translateTag, CEVEE_TAGS_EN } from '../constants/tagTranslations';

interface ProfileData {
  id: string;
  clerk_user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  subscription_type: string;
  subscription_period_end: number | null;  // Unix timestamp - Usage reset date
  next_billing_date: number | null;  // Unix timestamp - Next billing date
  cancel_at_period_end?: boolean;  // Is subscription canceled at period end
  is_recruiter: boolean;
  company_name: string | null;
  recruiter_background: string | null;
  profile_image_url: string | null;
  consents?: {
    [key: string]: {
      granted: boolean;
      granted_at?: string;
      revoked_at?: string;
      version?: string;
    };
  };
  consents_version?: string;
  consents_updated_at?: number;
  created_at: number;
  updated_at: number;
}

interface UsageStats {
  subscription_type: string;
  global: {
    used: number;
    limit: number;
    remaining: number;
  };
  features: {
    [key: string]: {
      used: number;
      limit: number;
      remaining: number;
    };
  };
  resets_at: string;
}

const RecruiterProfile: React.FC = () => {
  const { getToken, user, signOut, isSignedIn, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('recruiterProfile');
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Delete Account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Subscription state
  const [subscriptionInfo, setSubscriptionInfo] = useState<{
    plan: string;
    billing_cycle: string;
    status: string;
    next_billing_date: string;
    amount: string;
    cancel_at_period_end: boolean;
  } | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  
  // Usage dropdown
  const [usageExpanded, setUsageExpanded] = useState(false);
  const usageDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (usageDropdownRef.current && !usageDropdownRef.current.contains(event.target as Node)) {
        setUsageExpanded(false);
      }
    };
    if (usageExpanded) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [usageExpanded]);

  // Company data
  const [companyData, setCompanyData] = useState<any>(null);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [previewBioExpanded, setPreviewBioExpanded] = useState(false);
  const [previewLookingForExpanded, setPreviewLookingForExpanded] = useState(false);

  // Image upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const paymentsEnabled = usePaymentsVisibility();
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    recruiter_background: '',
    is_recruiter: true
  });

  // Consent state (loaded from profile.consents)
  const [consentGranted, setConsentGranted] = useState(false);

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  const apiCall = async (url: string, options: RequestInit = {}) => {
    const token = await getToken();
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
  };

  const loadProfile = async () => {
    if (!user?.userId) return;
    
    try {
      const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`);
      if (response.ok) {
        const data = await response.json();
        
        // ❌ Redirect explorers to normal profile page
        if (!data.is_recruiter) {
          showToast(t('errors.explorerRedirect'), 'info');
          navigate('/profile');
          return;
        }
        
        setProfile(data);
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          company_name: data.company_name || '',
          recruiter_background: data.recruiter_background || '',
          is_recruiter: data.is_recruiter ?? true
        });
        
        // Load consent from profile.consents
        const visibilityConsent = data.consents?.recruiter_profile_visibility;
        setConsentGranted(visibilityConsent?.granted || false);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsageStats = async () => {
    try {
      const response = await apiCall(`${apiBaseUrl}/api/v1/auth/usage-stats`);
      if (response.ok) {
        const data = await response.json();
        setUsageStats(data);
      }
    } catch (error) {
      console.error('Error loading usage stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const loadSubscription = async () => {
    try {
      setLoadingSubscription(true);
      
      if (!user?.userId) {
        console.warn('No user ID available for subscription check');
        return;
      }
      
      const response = await apiCall(`${apiBaseUrl}/api/v1/payment/subscription/${user.userId}`);
      
      if (response.ok) {
        const subscription = await response.json();
        console.debug('💳 Subscription loaded:', subscription);
        setSubscriptionInfo(subscription);
      } else {
        console.debug('No subscription found or error:', response.status);
      }
    } catch (error) {
      console.error('Error loading subscription:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const loadCompanyData = async () => {
    try {
      const response = await apiCall(`${apiBaseUrl}/api/v1/companies/my-company`);
      if (response.ok) {
        const data = await response.json();
        if (data.company) setCompanyData(data.company);
      }
    } catch { /* no company */ }
    finally { setLoadingCompany(false); }
  };

  useEffect(() => {
    if (user?.userId) {
      loadProfile();
      loadUsageStats();
      loadCompanyData();
    }
  }, [user?.userId]); // Only runs when userId changes

  // After payment success: refetch profile so subscription_type updates (webhook may lag)
  useEffect(() => {
    if (!location.state?.fromPayment || !user?.userId) return;
    const t1 = setTimeout(() => loadProfile(), 2000);
    const t2 = setTimeout(() => loadProfile(), 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location.state?.fromPayment, user?.userId]);
  
  useEffect(() => {
    if (profile && !subscriptionInfo && !loadingSubscription) {
      loadSubscription();
    }
  }, [profile]);

  const handleSave = async () => {
    if (!user?.userId) return;
    
    setSaving(true);
    setMessage(null);

    try {
      // Step 1: Update profile
      const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`, {
        method: 'PUT',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        setMessage({ type: 'error', text: t('messages.updateFailed') });
        setSaving(false);
        return;
      }

      const updatedProfile = await response.json();
      setProfile(updatedProfile);

      // Step 2: Update consent
      const consentResponse = await apiCall(
        `${apiBaseUrl}/api/v1/profiles/by-uuid/${user.userId}/consents`,
        {
          method: 'POST',
          body: JSON.stringify({
            consent_type: 'recruiter_profile_visibility',
            granted: consentGranted,
            version: '1.0'
          })
        }
      );

      if (!consentResponse.ok) {
        setMessage({ type: 'error', text: t('messages.consentFailed') });
        setSaving(false);
        return;
      }

      // Success! Reload profile to get updated consents with timestamp
      const reloadResponse = await apiCall(`${apiBaseUrl}/api/v1/profiles/${user.userId}`);
      if (reloadResponse.ok) {
        const reloadedData = await reloadResponse.json();
        setProfile(reloadedData);
        
        // Update consent state from reloaded profile
        const visibilityConsent = reloadedData.consents?.recruiter_profile_visibility;
        setConsentGranted(visibilityConsent?.granted || false);
      }
      
      setEditing(false);
      setMessage({ type: 'success', text: t('messages.updateSuccess') });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: t('messages.error') });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        company_name: profile.company_name || '',
        recruiter_background: profile.recruiter_background || '',
        is_recruiter: profile.is_recruiter ?? true
      });
    }
    // Reset consent to original value from profile
    if (profile?.consents?.recruiter_profile_visibility) {
      setConsentGranted(profile.consents.recruiter_profile_visibility.granted || false);
    }
    setEditing(false);
    setMessage(null);
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') {
      showToast(t('deleteAccount.confirmError'), 'error');
      return;
    }

    try {
      setIsDeleting(true);
      
      const response = await apiCall(`${apiBaseUrl}/api/v1/profiles/delete-account`, {
        method: 'DELETE',
      });

      if (response.ok) {
        showToast(t('deleteAccount.success'), 'success');
        // Sign out and redirect to home
        await signOut();
        navigate('/');
      } else {
        const errorData = await response.json();
        showToast(errorData.detail || t('deleteAccount.failed'), 'error');
      }
    } catch (error: any) {
      console.error('Error deleting account:', error);
      showToast(error.message || t('deleteAccount.failed'), 'error');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.userId) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      showToast('Nur JPEG, PNG oder WebP erlaubt', 'error');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('Bild muss kleiner als 5 MB sein', 'error');
      return;
    }

    setUploadingImage(true);
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('file', file);

      const resp = await fetch(`${apiBaseUrl}/api/v1/profiles/${user.userId}/profile-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (resp.ok) {
        const data = await resp.json();
        setProfile((prev) => prev ? { ...prev, profile_image_url: data.profile_image_url } : prev);
        showToast('Profilbild hochgeladen', 'success');
      } else {
        showToast('Upload fehlgeschlagen', 'error');
      }
    } catch {
      showToast('Upload fehlgeschlagen', 'error');
    }
    setUploadingImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getSubscriptionInfo = (type: string) => {
    const subscriptions: Record<string, { label: string; color: string }> = {
      'recruiter_free': { label: t('subscription.free'), color: 'blue' },
      'recruiter_premium': { label: t('subscription.premium'), color: 'purple' },
    };
    return subscriptions[type] || { label: t('subscription.default'), color: 'gray' };
  };

  if (!authLoading && !isSignedIn) {
    return <RecruiterLoggedOutGate returnTo="/recruiter-profile" />;
  }

  if (loading) {
    return (
      <RecruiterPageShell contentClassName="max-w-5xl">
        <div className="space-y-4 sm:space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-100 animate-pulse flex-shrink-0" />
              <div className="space-y-2 flex-1">
                <div className="h-4 w-36 bg-slate-100 rounded animate-pulse" />
                <div className="h-3 w-48 bg-slate-50 rounded animate-pulse" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
              <div className="space-y-4">
                <div className="h-3 w-24 bg-slate-100 rounded animate-pulse" />
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-8 bg-slate-50 rounded animate-pulse" />
                  <div className="h-8 bg-slate-50 rounded animate-pulse" />
                </div>
                <div className="h-8 bg-slate-50 rounded animate-pulse" />
                <div className="h-16 bg-slate-50 rounded animate-pulse" />
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="space-y-3">
                <div className="h-3 w-2/3 bg-slate-100 rounded animate-pulse" />
                <div className="h-10 bg-slate-50 rounded animate-pulse" />
                <div className="h-3 w-1/2 bg-slate-50 rounded animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </RecruiterPageShell>
    );
  }

  if (!profile) {
    return (
      <RecruiterPageShell contentClassName="max-w-5xl">
        <RecruiterInlineEmpty
          icon={User}
          title={t('errors.profileNotFound')}
        />
      </RecruiterPageShell>
    );
  }

  return (
    <RecruiterPageShell contentClassName="max-w-5xl">
        <AnimatePresence>
          {message && (
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              className={`mb-4 px-4 py-2.5 rounded-lg flex items-center text-sm ${
                message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {message.type === 'success' && <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />}
              {message.text}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-4 sm:mb-6 relative z-20">
          <div className="px-4 sm:px-6 py-4 sm:py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                <div className="relative flex-shrink-0">
                  {profile.profile_image_url ? (
                    <img
                      src={profile.profile_image_url.startsWith('/') ? `${apiBaseUrl}${profile.profile_image_url}` : profile.profile_image_url}
                      alt="Profile"
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover border border-slate-200 aspect-square"
                    />
                  ) : (
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-slate-900 flex items-center justify-center">
                      <span className="text-white font-semibold text-sm sm:text-base">
                        {profile.first_name?.[0] || 'R'}{profile.last_name?.[0] || ''}
                      </span>
                    </div>
                  )}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="absolute -bottom-0.5 -right-0.5 w-5 h-5 sm:w-6 sm:h-6 bg-white border border-slate-200 rounded-full flex items-center justify-center shadow-sm hover:bg-pink-50 hover:border-pink-200 transition-colors cursor-pointer"
                  >
                    {uploadingImage ? (
                      <Loader2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-pink-500 animate-spin" />
                    ) : (
                      <Camera className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-slate-500" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                <div className="min-w-0">
                  <h1 className="text-base sm:text-lg font-semibold text-slate-900 truncate">
                    {profile.first_name} {profile.last_name || t('header.defaultName')}
                  </h1>
                  <p className="text-slate-500 flex items-center text-xs sm:text-sm mt-0.5">
                    <Mail className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                    <span className="truncate">{profile.email}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {profile.subscription_type && (
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    profile.subscription_type === 'recruiter_premium'
                      ? 'bg-pink-600 text-white'
                      : 'bg-pink-50 text-pink-600'
                  }`}>
                    {getSubscriptionInfo(profile.subscription_type).label}
                  </span>
                )}

                <div className="relative" ref={usageDropdownRef}>
                  <button
                    onClick={() => setUsageExpanded(!usageExpanded)}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-slate-200 rounded-full text-xs font-medium text-slate-600 hover:bg-pink-50 hover:border-pink-200 transition-colors"
                  >
                    <BarChart3 className="w-3.5 h-3.5 text-pink-500" />
                    <span className="hidden sm:inline">{t('usage.title')}</span>
                    <motion.div animate={{ rotate: usageExpanded ? 180 : 0 }} transition={{ duration: 0.15 }}>
                      <ChevronDown className="w-3 h-3 text-slate-400" />
                    </motion.div>
                  </button>
                  <AnimatePresence>
                    {usageExpanded && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.12 }}
                        className="absolute top-full right-0 mt-1.5 w-64 bg-white rounded-xl shadow-xl border border-slate-200 z-[100] p-3"
                      >
                        {loadingStats ? (
                          <div className="flex items-center justify-center py-3">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-pink-200 border-t-pink-500"></div>
                          </div>
                        ) : usageStats ? (
                          <div className="space-y-2.5">
                            <div>
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-[11px] font-medium text-slate-600">{t('usage.totalMonthly')}</span>
                                <span className="text-[11px] text-slate-500">{usageStats.global.used}/{usageStats.global.limit}</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                <div className="bg-pink-500 h-1.5 rounded-full transition-all duration-300" style={{ width: `${Math.min((usageStats.global.used / usageStats.global.limit) * 100, 100)}%` }}></div>
                              </div>
                              <p className="text-[10px] text-slate-400 mt-1">
                                {usageStats.global.remaining} {t('usage.remaining')} · {t('usage.resets')} {new Date(usageStats.resets_at).toLocaleDateString(i18n.language)}
                              </p>
                            </div>
                            <div className="space-y-1.5 pt-2 border-t border-slate-100">
                              {Object.entries(usageStats.features)
                                .filter(([feature]) => ['candidate_search', 'reach_outs', 'candidate_analysis', 'ats_pushes'].includes(feature))
                                .map(([feature, stats]) => {
                                  const featureNames: Record<string, string> = {
                                    candidate_search: t('usage.features.candidateSearch'),
                                    candidate_analysis: t('usage.features.candidateAnalysis'),
                                    reach_outs: t('usage.features.reachOuts'),
                                    ats_pushes: t('usage.features.atsPushes'),
                                  };
                                  const featureName = featureNames[feature] || feature;
                                  const isDisabled = stats.limit === 0;
                                  return (
                                    <div key={feature} className="flex justify-between items-center text-[11px]">
                                      <span className="text-slate-500">{featureName}</span>
                                      <span className={`font-medium ${isDisabled ? 'text-slate-300' : stats.remaining === 0 ? 'text-red-500' : 'text-slate-600'}`}>
                                        {isDisabled ? '-' : `${stats.used}/${stats.limit}`}
                                      </span>
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 text-center py-2">Unable to load</p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {paymentsEnabled && profile.subscription_type === 'recruiter_free' && (
                  <button
                    onClick={() => navigate('/pricing')}
                    className="flex items-center gap-1 px-2.5 py-1 bg-pink-600 text-white rounded-full text-xs font-medium hover:bg-pink-700 transition-colors"
                  >
                    <Sparkles className="w-3 h-3" />
                    <span>Upgrade</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Profile Info (2/3) */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {editing ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-900">{t('form.sectionTitle')}</h3>
                  </div>
                  <div className="p-4 sm:p-6 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">{t('form.firstName')}</label>
                        <input
                          type="text"
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10 focus:border-slate-300 transition-colors"
                          placeholder={t('form.firstNamePlaceholder')}
                          maxLength={100}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1.5">{t('form.lastName')}</label>
                        <input
                          type="text"
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10 focus:border-slate-300 transition-colors"
                          placeholder={t('form.lastNamePlaceholder')}
                          maxLength={100}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">{t('form.companyName')}</label>
                      <input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10 focus:border-slate-300 transition-colors"
                        placeholder={t('form.companyNamePlaceholder')}
                        maxLength={200}
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1.5">{t('form.background')}</label>
                      <textarea
                        value={formData.recruiter_background}
                        onChange={(e) => setFormData({ ...formData, recruiter_background: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10 focus:border-slate-300 resize-none transition-colors"
                        placeholder={t('form.backgroundPlaceholder')}
                        maxLength={2000}
                      />
                      <p className="text-[11px] text-slate-400 mt-1">{t('form.backgroundHint')}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-900">{t('consent.title')}</h3>
                  </div>
                  <div className="p-4 sm:p-6">
                    <label className="flex items-start cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={consentGranted}
                        onChange={(e) => setConsentGranted(e.target.checked)}
                        className="mt-0.5 w-4 h-4 text-pink-600 border-slate-300 rounded focus:ring-pink-500/20 cursor-pointer"
                      />
                      <div className="ml-3 flex-1">
                        <p className="text-sm text-slate-700 leading-relaxed">{t('consent.mainText')}</p>
                        <p className="mt-1.5 text-[11px] text-slate-400">{t('consent.hint')}</p>
                      </div>
                    </label>
                  </div>
                </div>

                <div className="flex gap-2.5">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2 sm:py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 text-sm shadow-sm"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? t('form.saving') : t('form.saveChanges')}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="px-4 py-2 sm:py-2.5 border border-slate-200 text-slate-600 font-medium rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-2 text-sm"
                  >
                    <X className="w-4 h-4" />
                    {t('form.cancel')}
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                  <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-sm sm:text-base font-semibold text-slate-900">{t('form.sectionTitle')}</h3>
                    <button
                      onClick={() => setEditing(true)}
                      className="text-sm font-medium text-pink-600 hover:text-pink-700 transition-colors flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-pink-50"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">{t('header.editProfile')}</span>
                    </button>
                  </div>
                  <div className="divide-y divide-slate-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
                      <div className="px-4 sm:px-6 py-3 sm:py-4">
                        <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-0.5">{t('form.firstName')}</p>
                        <p className="text-sm font-medium text-slate-900">{profile.first_name || t('form.notSpecified')}</p>
                      </div>
                      <div className="px-4 sm:px-6 py-3 sm:py-4">
                        <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-0.5">{t('form.lastName')}</p>
                        <p className="text-sm font-medium text-slate-900">{profile.last_name || t('form.notSpecified')}</p>
                      </div>
                    </div>
                    <div className="px-4 sm:px-6 py-3 sm:py-4">
                      <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-0.5">{t('form.company')}</p>
                      <p className="text-sm font-medium text-slate-900">{profile.company_name || t('form.notSpecified')}</p>
                    </div>
                    <div className="px-4 sm:px-6 py-3 sm:py-4">
                      <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1">{t('form.backgroundView')}</p>
                      <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{profile.recruiter_background || t('form.notSpecified')}</p>
                    </div>
                    <div className="px-4 sm:px-6 py-3 sm:py-4">
                      <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">{t('consent.title')}</p>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          consentGranted
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'bg-slate-100 text-slate-500'
                        }`}>
                          {consentGranted ? <CheckCircle className="w-3 h-3" /> : null}
                          {consentGranted ? t('consent.granted') : t('consent.notGranted')}
                        </span>
                      </div>
                      {consentGranted && (
                        <p className="text-[11px] text-slate-400 mt-1">{t('consent.viewModeHint')}</p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Column - Company Preview, Billing, Danger Zone (1/3) */}
          <div className="space-y-4 sm:space-y-6">
            {loadingCompany ? (
              <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden">
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-slate-50 to-transparent z-10" />
                <div className="space-y-3">
                  <div className="h-3 w-2/3 bg-slate-100 rounded animate-pulse" />
                  <div className="h-3 w-full bg-slate-50 rounded animate-pulse" />
                  <div className="h-3 w-4/5 bg-slate-50 rounded animate-pulse" />
                </div>
              </div>
            ) : companyData ? (
              <>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium mb-1.5 px-0.5">
                    {i18n.language === 'de' ? 'Euer Unternehmensprofil (für Explorer:innen sichtbar)' : 'Your company profile (visible to explorers)'}
                  </p>
                  <div className="bg-white rounded-xl shadow-md overflow-hidden border border-slate-200">
                    <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-2 flex items-start gap-2 sm:gap-3">
                      {companyData.logo_url ? (
                        <img src={`${apiBaseUrl}/api/v1/companies/logos/${companyData.logo_url}`} alt="" className="w-12 h-12 rounded-lg object-cover shadow-sm border border-slate-100" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-100 to-pink-200 flex items-center justify-center shadow-sm">
                          <Building2 className="w-6 h-6 text-pink-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-slate-900 text-sm truncate">{companyData.name}</h4>
                        {companyData.industry && <p className="text-[10px] text-slate-500 mt-0.5">{companyData.industry}</p>}
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5 text-[10px] text-slate-400">
                          {companyData.location && <span className="flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{companyData.location}</span>}
                          {companyData.company_size && <span className="flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{companyData.company_size}</span>}
                          {companyData.founded_year && <span className="flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" />{companyData.founded_year}</span>}
                        </div>
                        {companyData.cevee_tags?.length > 0 && (
                          <div className="flex flex-wrap gap-0.5 mt-1">
                            {companyData.cevee_tags.slice(0, 5).map((tag: string) => (
                              <span key={tag} className="text-[8px] px-1 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
                                {translateTag(tag, i18n.language, CEVEE_TAGS_EN)}
                              </span>
                            ))}
                            {companyData.cevee_tags.length > 5 && (
                              <span className="text-[8px] px-1 py-0.5 rounded-full bg-slate-100 text-slate-400">+{companyData.cevee_tags.length - 5}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {companyData.bio && (
                      <div className="px-4">
                        <h4 className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
                          {i18n.language === 'de' ? 'Über uns' : 'About'}
                        </h4>
                        <p className={`text-xs text-slate-600 leading-relaxed whitespace-pre-line ${!previewBioExpanded ? 'line-clamp-3' : ''}`}>{companyData.bio}</p>
                        {((companyData.bio?.length || 0) > 120 || (companyData.bio?.match(/\n/g) || []).length >= 3) && (
                          <button onClick={() => setPreviewBioExpanded(!previewBioExpanded)} className="text-[10px] text-pink-500 hover:text-pink-600 font-medium mt-0.5">
                            {previewBioExpanded ? (i18n.language === 'de' ? 'Weniger' : 'Less') : (i18n.language === 'de' ? 'Mehr lesen' : 'Read more')}
                          </button>
                        )}
                      </div>
                    )}

                    {companyData.looking_for && (
                      <div className="px-4 pt-1.5">
                        <h4 className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">
                          {i18n.language === 'de' ? 'Wir suchen' : 'Looking for'}
                        </h4>
                        <p className={`text-xs text-slate-600 leading-relaxed whitespace-pre-line ${!previewLookingForExpanded ? 'line-clamp-2' : ''}`}>{companyData.looking_for}</p>
                        {((companyData.looking_for?.length || 0) > 80 || (companyData.looking_for?.match(/\n/g) || []).length >= 2) && (
                          <button onClick={() => setPreviewLookingForExpanded(!previewLookingForExpanded)} className="text-[10px] text-pink-500 hover:text-pink-600 font-medium mt-0.5">
                            {previewLookingForExpanded ? (i18n.language === 'de' ? 'Weniger' : 'Less') : (i18n.language === 'de' ? 'Mehr lesen' : 'Read more')}
                          </button>
                        )}
                      </div>
                    )}

                    <div className="px-4 py-3 flex items-center">
                      <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-slate-50 text-slate-400 border-2 border-slate-200 cursor-default">
                        <Heart className="w-3 h-3" /> {i18n.language === 'de' ? 'Interessiert?' : 'Interested?'}
                      </span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => navigate('/company/manage')}
                  className="w-full py-2 rounded-lg text-sm font-medium text-pink-600 border border-pink-200 hover:bg-pink-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                  {i18n.language === 'de' ? 'Company bearbeiten' : 'Edit company'}
                </button>
              </>
            ) : (
              <div className="bg-white rounded-xl p-5 border border-dashed border-slate-300 text-center">
                <div className="w-12 h-12 rounded-xl bg-pink-50 flex items-center justify-center mx-auto mb-3">
                  <Building2 className="w-6 h-6 text-pink-500" />
                </div>
                <h3 className="text-sm font-bold text-slate-900 mb-1">
                  {i18n.language === 'de' ? 'Kein Unternehmen' : 'No company yet'}
                </h3>
                <p className="text-xs text-slate-500 mb-4">
                  {i18n.language === 'de'
                    ? 'Erstelle dein Unternehmensprofil, damit Kandidat:innen euch entdecken können.'
                    : 'Create your company profile so candidates can discover you.'}
                </p>
                <button
                  onClick={() => navigate('/company/manage')}
                  className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg bg-slate-900 text-white shadow-sm hover:bg-slate-800 transition-colors"
                >
                  <Building2 className="w-4 h-4" />
                  {i18n.language === 'de' ? 'Company erstellen' : 'Create company'}
                </button>
              </div>
            )}

            {paymentsEnabled && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="px-4 sm:px-5 py-3 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-900">{t('billing.title')}</h3>
                </div>
                {profile ? (
                  <div className="p-4 sm:p-5 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-slate-500">{t('billing.currentPlan')}</span>
                      <span className="text-xs font-semibold text-pink-600">
                        {profile.subscription_type === 'recruiter_premium' ? t('subscription.premium') :
                         profile.subscription_type === 'recruiter_free' ? t('subscription.free') :
                         t('subscription.default')}
                      </span>
                    </div>
                    <div className="space-y-1.5">
                      {(profile.subscription_type === 'recruiter_free') ? (
                        <button
                          onClick={() => navigate('/pricing')}
                          className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2 px-4 rounded-xl shadow-sm transition-colors flex items-center justify-center gap-1.5 text-sm"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          {t('billing.upgradeToPremium')}
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate('/settings/billing')}
                          className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2 px-4 rounded-xl transition-all flex items-center justify-center gap-1.5 text-sm"
                        >
                          {t('billing.manageBilling')}
                        </button>
                      )}
                      <button
                        onClick={() => navigate('/pricing')}
                        className="w-full text-slate-500 hover:text-pink-500 font-medium py-1.5 px-3 rounded-lg text-xs hover:bg-pink-50 transition-colors"
                      >
                        {t('billing.viewAllPlans')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 sm:p-5">
                    <button
                      onClick={() => navigate('/pricing')}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2 px-4 rounded-xl shadow-sm transition-colors flex items-center justify-center gap-1.5 text-sm"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {t('billing.upgradeToPremium')}
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                  <span className="text-xs font-medium text-slate-600">{t('deleteAccount.dangerZoneTitle')}</span>
                </div>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="text-xs font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-2.5 py-1 rounded-md transition-colors"
                >
                  {t('deleteAccount.deleteButton')}
                </button>
              </div>
            </div>
          </div>
        </div>

      {/* Delete Account Confirmation Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[10001] p-3 sm:p-4"
            onClick={() => !isDeleting && setShowDeleteModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-slate-900">
                  {t('deleteAccount.modalTitle')}
                </h3>
              </div>

              <div className="mb-6">
                <p className="text-slate-700 mb-4">
                  {t('deleteAccount.modalDescription')}
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 mb-4">
                  <li>{t('deleteAccount.item1')}</li>
                  <li>{t('deleteAccount.item2')}</li>
                  <li>{t('deleteAccount.item3')}</li>
                  <li>{t('deleteAccount.item4')}</li>
                  <li>{t('deleteAccount.item5')}</li>
                </ul>
                <p className="text-sm font-semibold text-red-600">
                  {t('deleteAccount.warningFinal')}
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('deleteAccount.confirmLabel')} <span className="font-bold">DELETE</span> {t('deleteAccount.toConfirm')}
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  disabled={isDeleting}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-red-500/30 focus:border-red-300 disabled:opacity-50 transition-colors"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isDeleting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>{t('deleteAccount.deleting')}</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>{t('deleteAccount.confirmButton')}</span>
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {t('deleteAccount.cancel')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </RecruiterPageShell>
  );
};

export default RecruiterProfile;
